/**
 * Flattens drawable paths into polylines, in element-local space.
 *
 * Hit testing and arrow binding both consume these, so there is exactly one hit
 * test for every shape rather than one per type, and it can never disagree with
 * what was actually drawn.
 */

import type { Drawable, PathCmd } from './drawable';
import type { Vec } from './transform';

export interface Polyline {
  readonly points: readonly Vec[];
  readonly closed: boolean;
  /** Only closed, filled paths get an interior hit. */
  readonly filled: boolean;
  readonly strokeWidth: number;
}

/** Segments per cubic. 16 keeps the flattening error well under a pixel. */
const CUBIC_STEPS = 16;

function cubicAt(p0: Vec, c1: Vec, c2: Vec, p1: Vec, t: number): Vec {
  const mt = 1 - t;
  const a = mt * mt * mt;
  const b = 3 * mt * mt * t;
  const c = 3 * mt * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * c1.x + c * c2.x + d * p1.x,
    y: a * p0.y + b * c1.y + c * c2.y + d * p1.y,
  };
}

export function flattenCmds(cmds: readonly PathCmd[]): Vec[] {
  const out: Vec[] = [];
  let cursor: Vec = { x: 0, y: 0 };
  let start: Vec = { x: 0, y: 0 };
  for (const cmd of cmds) {
    switch (cmd.c) {
      case 'M':
        cursor = { x: cmd.p[0], y: cmd.p[1] };
        start = cursor;
        out.push(cursor);
        break;
      case 'L':
        cursor = { x: cmd.p[0], y: cmd.p[1] };
        out.push(cursor);
        break;
      case 'C': {
        const c1 = { x: cmd.c1[0], y: cmd.c1[1] };
        const c2 = { x: cmd.c2[0], y: cmd.c2[1] };
        const end = { x: cmd.p[0], y: cmd.p[1] };
        for (let i = 1; i <= CUBIC_STEPS; i++) {
          out.push(cubicAt(cursor, c1, c2, end, i / CUBIC_STEPS));
        }
        cursor = end;
        break;
      }
      case 'Z':
        out.push(start);
        cursor = start;
        break;
    }
  }
  return out;
}

export function flattenDrawable(drawable: Drawable): Polyline[] {
  const out: Polyline[] = [];
  for (const op of drawable.ops) {
    if (op.k !== 'path') continue;
    const points = flattenCmds(op.cmds);
    if (points.length < 2) continue;
    out.push({
      points,
      closed: op.closed,
      filled: op.fill !== null && op.closed,
      strokeWidth: op.stroke?.width ?? 0,
    });
  }
  return out;
}
